import type { CustomAttributeTagRenameResponse } from "@eu.mdotm.api/custom-attributes";
import type { ConceptDefinitionDTODataTypeEnum, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import type { TaskQueue } from "$root/utils";
import { investmentCustomAttributesRepository } from "../../customAttributes/repository";
import type { ParameterizedConceptDTOWithMandatoryUuid } from "../ctx";
import { workspaceRepository } from "../repository";
import { isManagedConcept } from "../spec-gaps";
import type { WorkspaceTableSettingsColumnProperty } from "../workspaceTable/WorkspaceTableSettingsColumnProperty";

// --- Types ---

export type PrivateParametrizedConcept = ParameterizedConceptDTOWithMandatoryUuid;
export type SharedParametrizedConcept = ParameterizedConceptDTOWithMandatoryUuid;

// --- Helpers ---

function toMandatoryUuid(concepts: ParameterizedConceptDTO[]): SharedParametrizedConcept[] {
	return concepts.filter((c): c is SharedParametrizedConcept => c.uuid != null);
}

const CUSTOM_ATTRIBUTE_DATA_TYPES: ConceptDefinitionDTODataTypeEnum[] = [
	"CUSTOM_ATTRIBUTE_TAG",
	"CUSTOM_ATTRIBUTE_DATE",
	"CUSTOM_ATTRIBUTE_NUMBER",
	"CUSTOM_ATTRIBUTE_TEXT",
];

/** Check by dataType (from ConceptDefinitionDTO) — use in UI to decide rendering */
export function isCustomAttributeConcept(dataType: ConceptDefinitionDTODataTypeEnum): boolean {
	return CUSTOM_ATTRIBUTE_DATA_TYPES.includes(dataType);
}

/** Check by conceptId prefix — use internally for routing API calls */
export function isCustomAttributeConceptId(conceptId: string): boolean {
	return conceptId.startsWith("customAttributeId:");
}

export function extractCustomAttributeUuid(conceptId: string): string {
	return conceptId.substring("customAttributeId:".length).trim();
}

/**
 * Defense in depth (MDOT-24352): a managed (system-shared) concept can never be config-edited or
 * deleted. The UI hides those actions; this guard protects every remaining call path.
 */
async function assertNotManagedConcept(conceptId: string | undefined, operation: string): Promise<void> {
	if (!conceptId) {
		return;
	}

	const conceptMap = await workspaceRepository.getConceptMapDefinitions.fetchQuery();
	const definition = conceptMap.get(conceptId);
	if (isManagedConcept(definition)) {
		throw new Error(
			`Cannot ${operation} concept "${definition?.defaultLabel ?? conceptId}": it is managed by the system, only column visibility, column order and attribute values can be changed`,
		);
	}
}

export function columnPropertyToCustomAttributeDto(data: WorkspaceTableSettingsColumnProperty) {
	return {
		name: data.name,
		attributeType: data.attributeType,
		description: data.description,
		tags:
			data.attributeType === "TAG"
				? data.tagOptions?.map((opt) => ({ value: opt.label, color: opt.color }))
				: undefined,
		multiTag: data.attributeType === "TAG" ? data.allowMultipleTags : undefined,
	};
}

// --- Service factory ---

/**
 * What the service needs from the workspace, stated as three functions instead of taken as the context.
 *
 * The point is `publish`. This service used to own six writes to `sharedParametrizedConcepts$` — a second
 * mutation authority next to the controllers — and under `dispatch` it is the implementation of the
 * shared-concept *effects* instead: it performs the request and publishes what the API answered, and the
 * store is what folds that into the pool (`sharedConceptsReplaced`). Nothing here writes state any more.
 */
export type SharedConceptDeps = {
	/** Sequences the refetch behind the workspace PUTs, exactly as `ctx.taskQueue$` did. */
	taskQueue: TaskQueue;
	/** The shared half of the pool, looked up by uuid. */
	find(parameterizedConceptUuid: string): SharedParametrizedConcept | undefined;
	/** The tenant's shared pool as its API reports it. */
	publish(concepts: SharedParametrizedConcept[]): void;
};

export function createSharedConceptService(deps: SharedConceptDeps) {
	const { publish, taskQueue } = deps;

	function isShared(parameterizedConceptUuid: string): boolean {
		return deps.find(parameterizedConceptUuid) != null;
	}

	function scheduleRefetch() {
		taskQueue.enqueue(() =>
			workspaceRepository.getSharedConcepts.fetchQuery().then(({ sharedConcepts }) => {
				publish(toMandatoryUuid(sharedConcepts ?? []));
			}),
		);
	}

	/**
	 * Catch the client up with a custom attribute the backend has just rewritten.
	 *
	 * Every write to a custom attribute propagates server-side to the concept definition, the shared
	 * concepts and the values on the rows, so all four reads are stale at once and the same four have to
	 * be refreshed whether the write renamed the attribute, its options, or changed its tags.
	 */
	async function refreshCustomAttributeConcept(conceptId: string): Promise<void> {
		await workspaceRepository.getConceptMapDefinitions.fetchQuery();
		const { sharedConcepts } = await workspaceRepository.getSharedConcepts.fetchQuery();
		publish(toMandatoryUuid(sharedConcepts ?? []));

		workspaceRepository.invalidate([...workspaceRepository.getConceptOptions.partialQueryKey, conceptId]);
		workspaceRepository.invalidate([
			...workspaceRepository.getConceptCustomAttributeTagOptions.partialQueryKey,
			conceptId,
		]);
		workspaceRepository.invalidate([...workspaceRepository.getWorkspaceData.fullQueryKey, "grid"]);
	}

	return {
		isShared,

		/**
		 * Share an already-constructed parametrized concept.
		 * Used when creating a column from a non-private concept definition (conceptDef.shared === true).
		 */
		async createSharedConcept(concept: ParameterizedConceptDTOWithMandatoryUuid): Promise<SharedParametrizedConcept[]> {
			const sharedConcepts = await workspaceRepository.createSharedParametrizedConcept(concept);
			const result = toMandatoryUuid(sharedConcepts);
			scheduleRefetch();
			return result;
		},

		/**
		 * Create a custom attribute property (TAG / DATE / NUMBER / TEXT) and register it as a shared concept.
		 * The backend auto-creates the concept definition after creating the custom attribute.
		 */
		async createCustomAttributeConcept(
			data: WorkspaceTableSettingsColumnProperty,
		): Promise<{ concept: SharedParametrizedConcept; customAttributeUuid: string }> {
			const customAttributePayload = columnPropertyToCustomAttributeDto(data);
			const customAttribute = await investmentCustomAttributesRepository.createCustomAttribute(customAttributePayload);

			if (!customAttribute.uuid) {
				throw new Error("Unable to receive custom attribute uuid");
			}

			// Re-fetch concept definitions — backend auto-creates the definition as a side effect
			const conceptMap = await workspaceRepository.getConceptMapDefinitions.fetchQuery();
			const conceptDefinition = conceptMap.get(`customAttributeId: ${customAttribute.uuid}`);

			if (!conceptDefinition) {
				throw new Error("Unable to find created custom attribute in concept definition");
			}

			const newConcept: SharedParametrizedConcept = {
				uuid: crypto.randomUUID(),
				conceptId: conceptDefinition.id!,
				label: customAttribute.name ?? data.name,
				source: "CURRENT",
				representationMode: "ABSOLUTE",
			};

			const sharedConcepts = await workspaceRepository.createSharedParametrizedConcept(newConcept);
			const result = toMandatoryUuid(sharedConcepts);
			// Deliberately *not* published here. This is the one write-behind the reducer could not do
			// local-first — the column is holding a placeholder concept until `conceptReconciled` swaps it for
			// the one below — and publishing the tenant's pool now would be a `sharedConceptsReplaced` that does
			// not contain the placeholder, i.e. would take the new column with it. The refetch is enqueued
			// instead, and lands after the reconciliation.
			scheduleRefetch();

			const created = result.find((c) => c.conceptId === newConcept.conceptId) ?? newConcept;
			return { concept: created, customAttributeUuid: customAttribute.uuid };
		},

		/**
		 * Update a shared concept in-place.
		 * For custom attributes: delegates to investmentCustomAttributesRepository and relies on
		 * backend propagation to the shared concept. For regular shared concepts: uses editSharedParametrizedConcept.
		 */
		async updateSharedConcept(
			parameterizedConceptUuid: string,
			updatedConcept: ParameterizedConceptDTOWithMandatoryUuid,
		): Promise<void> {
			const sharedConcept = deps.find(parameterizedConceptUuid);
			if (!sharedConcept?.conceptId) {
				throw new Error(`No shared concept found for uuid: ${parameterizedConceptUuid}`);
			}

			if (isCustomAttributeConceptId(sharedConcept.conceptId)) {
				throw new Error("Use updateCustomAttributeConcept to update custom attribute metadata");
			}

			const result = await workspaceRepository.editSharedParametrizedConcept(updatedConcept);
			publish(toMandatoryUuid(result));
			scheduleRefetch();
		},

		/**
		 * Update the metadata (name, tags, etc.) of a custom attribute concept.
		 * Backend propagates changes to the concept definition and shared concepts as a side effect.
		 */
		async updateCustomAttributeConcept(
			parameterizedConceptUuid: string,
			data: WorkspaceTableSettingsColumnProperty,
		): Promise<void> {
			const sharedConcept = deps.find(parameterizedConceptUuid);
			if (!sharedConcept?.conceptId) {
				throw new Error("Missing shared concept for custom property update");
			}

			await assertNotManagedConcept(sharedConcept.conceptId, "update");

			const customAttributeUuid = extractCustomAttributeUuid(sharedConcept.conceptId);
			const payload = columnPropertyToCustomAttributeDto(data);
			await investmentCustomAttributesRepository.updateCustomAttribute(customAttributeUuid, payload);

			await refreshCustomAttributeConcept(sharedConcept.conceptId);
		},

		/**
		 * Rename one tag option of a custom attribute.
		 *
		 * Separate from {@link updateCustomAttributeConcept} because the update endpoint identifies options by
		 * name: handed a renamed one it removes the tag from every portfolio carrying it and the criterion from
		 * the filters of every workspace of every user of the client. The rename endpoint replaces the value in
		 * one transaction; the counts it answers with are what the portfolios and filters it touched.
		 */
		async renameCustomAttributeTagOption(
			parameterizedConceptUuid: string,
			currentTagValue: string,
			newTagValue: string,
		): Promise<CustomAttributeTagRenameResponse> {
			const sharedConcept = deps.find(parameterizedConceptUuid);
			if (!sharedConcept?.conceptId) {
				throw new Error("Missing shared concept for tag option rename");
			}

			await assertNotManagedConcept(sharedConcept.conceptId, "rename a tag option of");

			const result = await investmentCustomAttributesRepository.renameTagOption({
				attributeUuid: extractCustomAttributeUuid(sharedConcept.conceptId),
				currentTagValue,
				newTagValue,
			});

			await refreshCustomAttributeConcept(sharedConcept.conceptId);

			return result;
		},

		/**
		 * Delete a shared concept. For custom attributes, the backend implicitly deletes
		 * the underlying custom attribute and concept definition as a side effect.
		 *
		 * Takes the concept rather than its uuid — the one asymmetry this service used to have with
		 * `createSharedConcept`, and the reason a shared-column delete once stopped reaching the backend
		 * entirely. `removeColumn {shared: true}` drops the concept from the pool and *then* asks for this, so
		 * there is nothing left for `deps.find` to resolve; the reducer resolves it while it still can and
		 * sends it along. See the note on the effect.
		 */
		async deleteSharedConcept(concept: SharedParametrizedConcept): Promise<void> {
			await assertNotManagedConcept(concept.conceptId, "delete");

			await workspaceRepository.deleteSharedParametrizedConcept({ sharedConcepts: [concept] });

			// The reducer has already dropped it locally (see `removeColumn`'s shared branch), so this only has
			// to catch the pool up with what the tenant's API now reports.
			scheduleRefetch();
		},
	};
}

export type SharedConceptService = ReturnType<typeof createSharedConceptService>;
